import { useMemo } from 'react';
import Plot from 'react-plotly.js';
import type { Data, Layout } from 'plotly.js';
import type { TestChartData } from '@/lib/impedance';
import { formatLegendLabel } from './BodePlot';

export interface NyquistPlotProps {
  testData: TestChartData[];
  viewMode: 'baselines' | 'specimens' | 'both';
  visibleChannels: Set<string>;
  colorMap: Map<string, string>;
  onPlotRef?: (el: HTMLElement | null) => void;
}

export function NyquistPlot({
  testData,
  viewMode,
  visibleChannels,
  colorMap,
  onPlotRef,
}: NyquistPlotProps) {
  const traces = useMemo<Data[]>(() => {
    const result: Data[] = [];
    const useWebGL = testData.length > 10;
    const traceType = useWebGL ? 'scattergl' : 'scatter';

    for (const test of testData) {
      const color = colorMap.get(test.testId) ?? '#888';

      const measurementTypes: ('baseline' | 'specimen')[] =
        viewMode === 'baselines'
          ? ['baseline']
          : viewMode === 'specimens'
            ? ['specimen']
            : ['baseline', 'specimen'];

      for (const mtype of measurementTypes) {
        const measurementData = mtype === 'baseline' ? test.baseline : test.specimen;
        if (!measurementData) continue;

        for (const channel of measurementData.channels) {
          if (!visibleChannels.has(channel.channelId)) continue;

          const baseLabel = `${test.label} ${channel.channelId} ${mtype === 'baseline' ? 'BL' : 'SP'}`;
          const label = formatLegendLabel(baseLabel, test.notes);
          const isFirstTraceForTest = !result.some((t) => (t as any).legendgroup === test.testId);
          const notesLine = test.notes ? `Note: ${test.notes}<br>` : '';

          result.push({
            x: channel.points.map((p) => p.zReal),
            y: channel.points.map((p) => -p.zImag),
            customdata: channel.points.map((p) => p.frequency),
            type: traceType as 'scatter' | 'scattergl',
            mode: 'lines+markers',
            name: label,
            line: {
              color,
              dash: mtype === 'baseline' ? 'solid' : 'dash',
              width: 1.5,
            },
            marker: { size: 4, color },
            legendgroup: test.testId,
            legendgrouptitle: isFirstTraceForTest ? { text: test.label } : undefined,
            hovertemplate:
              `<b>${baseLabel}</b><br>` +
              notesLine +
              `Z_real: %{x:.1f} Ω<br>` +
              `-Z_imag: %{y:.1f} Ω<br>` +
              `Freq: %{customdata:.2f} Hz<br>` +
              `<extra></extra>`,
            showlegend: true,
          } as Data);
        }
      }
    }

    return result;
  }, [testData, viewMode, visibleChannels, colorMap]);

  const layout = useMemo<Partial<Layout>>(
    () => ({
      xaxis: {
        title: { text: 'Z_real (Ω)' },
        scaleanchor: 'y',
        scaleratio: 1,
        autorange: true,
      },
      yaxis: {
        title: { text: '-Z_imag (Ω)' },
        autorange: true,
      },
      legend: {
        orientation: 'h',
        y: -0.25,
        x: 0,
        groupclick: 'toggleitem',
      },
      hovermode: 'closest',
      margin: { t: 30, r: 60, b: 120, l: 60 },
      autosize: true,
    }),
    [],
  );

  if (testData.length === 0) {
    return null;
  }

  return (
    <Plot
      data={traces}
      layout={layout}
      style={{ width: '100%', height: '100%' }}
      useResizeHandler
      config={{ responsive: true, displayModeBar: true, displaylogo: false, scrollZoom: true }}
      onInitialized={(_, graphDiv) => onPlotRef?.(graphDiv)}
      onPurge={() => onPlotRef?.(null)}
    />
  );
}
